import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import type { Call, QueueSnapshot, TelephonyConfig } from '../lib/types';
import { useAuth } from '../state/AuthContext';
import { useRealtime } from '../state/hooks';
import type { RealtimeEvent } from '../lib/realtime';
import type { RegistrationState, TelephonyDriver } from './driver';
import { MockDriver } from './mockDriver';

/**
 * The softphone, as the rest of the app sees it.
 *
 * Every action does two things in a fixed order: it changes the CAD record via
 * the API, then it moves the media. Claiming the record first is what makes a
 * race between two call takers safe — the loser gets a 409 and never picks up
 * an audio path for a call that is not theirs.
 */

export interface SoftphoneState {
  available: boolean;
  driver: 'sip' | 'mock' | 'webrtc' | null;
  registration: RegistrationState;
  registrationDetail: string | null;
  queue: QueueSnapshot;
  activeCall: Call | null;
  muted: boolean;
  recording: boolean;
  error: string | null;
  refreshQueue: () => void;
  answer: (callId: string) => Promise<void>;
  hangup: () => Promise<void>;
  setHold: (held: boolean) => Promise<void>;
  toggleMute: () => Promise<void>;
  dial: (destination: string, incidentId?: string | null) => Promise<void>;
  sendDtmf: (digits: string) => Promise<void>;
  transfer: (target: { userId?: string; extension?: string; number?: string }, mode: 'blind' | 'attended') => Promise<void>;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  attachToIncident: (incidentId: string) => Promise<void>;
  setPresence: (state: 'available' | 'unavailable' | 'wrap_up') => Promise<void>;
}

const EMPTY_QUEUE: QueueSnapshot = { waiting: [], active: [], longestWaitSeconds: 0 };

const SoftphoneContext = createContext<SoftphoneState | null>(null);

export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const { user, can } = useAuth();
  const canHandleCalls = can('calls.handle');

  const [driver, setDriver] = useState<TelephonyDriver | null>(null);
  const [registration, setRegistration] = useState<RegistrationState>('idle');
  const [registrationDetail, setRegistrationDetail] = useState<string | null>(null);
  const [queue, setQueue] = useState<QueueSnapshot>(EMPTY_QUEUE);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [muted, setMuted] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordingIdRef = useRef<string | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const driverRef = useRef<TelephonyDriver | null>(null);
  const activeCallRef = useRef<Call | null>(null);
  activeCallRef.current = activeCall;

  const refreshQueue = useCallback(() => {
    if (!canHandleCalls && !can('calls.read')) return;
    void api
      .get<QueueSnapshot>('/calls/queue')
      .then(setQueue)
      .catch(() => undefined);
  }, [canHandleCalls, can]);

  // ------------------------------------------------------------ driver lifecycle

  useEffect(() => {
    if (!user || !canHandleCalls) return;
    let cancelled = false;
    let instance: TelephonyDriver | null = null;

    void (async () => {
      try {
        const config = await api.get<TelephonyConfig>('/telephony/config');
        if (cancelled) return;

        // The one place the driver name is read. Everything above this line
        // treats the two identically. The SIP stack is a large dependency and
        // is only fetched when a real trunk is actually configured, so a
        // sandbox or a non-phone role never downloads it.
        if (config.driver === 'sip') {
          const { SipDriver } = await import('./sipDriver');
          instance = new SipDriver();
        } else if (config.driver === 'webrtc') {
          const { WebRtcDriver } = await import('./webrtcDriver');
          instance = new WebRtcDriver();
        } else {
          instance = new MockDriver();
        }
        if (cancelled) return;
        driverRef.current = instance;
        setDriver(instance);

        await instance.start(config, {
          onRegistration: (state, detail) => {
            setRegistration(state);
            setRegistrationDetail(detail ?? null);
            // The admin health view is only useful if handsets report in.
            void api
              .post('/telephony/registration', {
                trunkId: config.trunks[0]?.id ?? null,
                registered: state === 'registered',
                detail: detail?.slice(0, 500),
              })
              .catch(() => undefined);
          },
          onIncoming: () => refreshQueue(),
          onEstablished: () => refreshQueue(),
          onEnded: () => {
            setRecording(false);
            refreshQueue();
          },
          onRemoteStream: (_id, stream) => {
            if (audioRef.current) {
              audioRef.current.srcObject = stream;
              void audioRef.current.play().catch(() => undefined);
            }
          },
        });
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      void instance?.stop();
      driverRef.current = null;
    };
  }, [user, canHandleCalls, refreshQueue]);

  // ------------------------------------------------------------ live queue

  const onRealtime = useCallback(
    (event: RealtimeEvent) => {
      if (event.type === 'queue.snapshot') {
        setQueue(event.payload as QueueSnapshot);
        return;
      }
      if (event.topic !== 'calls') return;

      const call = event.payload as Call | null;
      if (call?.agent_id && call.agent_id === user?.id) {
        setActiveCall(call.ended_at ? null : call);
      }
      if (event.type === 'call.ended' && call?.id === activeCallRef.current?.id) {
        setActiveCall(null);
      }
      refreshQueue();
    },
    [user?.id, refreshQueue],
  );

  useRealtime(['calls', 'presence'], onRealtime, refreshQueue);

  useEffect(() => {
    refreshQueue();
  }, [refreshQueue]);

  // On load, re-adopt a call this agent was already on — a browser reload
  // mid-call must not lose the record of it.
  useEffect(() => {
    if (!user || !canHandleCalls) return;
    void api
      .get<{ calls: Call[] }>('/calls?live=true&limit=5')
      .then(({ calls }) => {
        const mine = calls.find((call) => call.agent_id === user.id && !call.ended_at);
        if (mine) setActiveCall(mine);
      })
      .catch(() => undefined);
  }, [user, canHandleCalls]);

  // ------------------------------------------------------------ actions

  const answer = useCallback(
    async (callId: string) => {
      setError(null);
      // Claim the record first: the loser of a race gets a 409 here and never
      // touches the audio path.
      const { call } = await api.post<{ call: Call }>(`/calls/${callId}/answer`);
      setActiveCall(call);
      try {
        await driverRef.current?.answer(call.sip_call_id);
      } catch (err) {
        // The record is ours but the media failed — release it rather than
        // leaving a caller connected to a silent agent.
        await api.post(`/calls/${callId}/hangup`, { reason: 'media_failed' }).catch(() => undefined);
        setActiveCall(null);
        throw err;
      }
      refreshQueue();
    },
    [refreshQueue],
  );

  const hangup = useCallback(async () => {
    const call = activeCallRef.current;
    if (!call) return;
    await driverRef.current?.hangup(call.sip_call_id).catch(() => undefined);
    await api.post(`/calls/${call.id}/hangup`, { reason: 'hangup' });
    setActiveCall(null);
    setRecording(false);
    refreshQueue();
  }, [refreshQueue]);

  const setHold = useCallback(async (held: boolean) => {
    const call = activeCallRef.current;
    if (!call) return;
    await driverRef.current?.hold(call.sip_call_id, held);
    const { call: updated } = await api.post<{ call: Call }>(`/calls/${call.id}/state`, {
      action: held ? 'hold' : 'unhold',
    });
    setActiveCall(updated);
  }, []);

  const toggleMute = useCallback(async () => {
    const next = !muted;
    await driverRef.current?.mute(next);
    setMuted(next);
  }, [muted]);

  const dial = useCallback(
    async (destination: string, incidentId?: string | null) => {
      setError(null);
      const { call } = await api.post<{ call: Call }>('/calls/dial', { destination, incidentId });
      setActiveCall(call);
      try {
        await driverRef.current?.dial(destination);
      } catch (err) {
        await api.post(`/calls/${call.id}/hangup`, { reason: 'dial_failed' }).catch(() => undefined);
        setActiveCall(null);
        throw err;
      }
    },
    [],
  );

  const sendDtmf = useCallback(async (digits: string) => {
    const call = activeCallRef.current;
    if (!call) return;
    await driverRef.current?.sendDtmf(call.sip_call_id, digits);
    await api.post(`/calls/${call.id}/dtmf`, { digits });
  }, []);

  const transfer = useCallback(
    async (target: { userId?: string; extension?: string; number?: string }, mode: 'blind' | 'attended') => {
      const call = activeCallRef.current;
      if (!call) return;
      const destination = target.extension ?? target.number;
      if (destination) await driverRef.current?.transfer(call.sip_call_id, destination);
      await api.post(`/calls/${call.id}/transfer`, {
        mode,
        targetUserId: target.userId ?? null,
        targetExtension: target.extension ?? null,
        targetNumber: target.number ?? null,
      });
      if (mode === 'blind') setActiveCall(null);
      refreshQueue();
    },
    [refreshQueue],
  );

  const attachToIncident = useCallback(async (incidentId: string) => {
    const call = activeCallRef.current;
    if (!call) return;
    const { call: updated } = await api.post<{ call: Call }>(`/calls/${call.id}/attach`, { incidentId });
    setActiveCall(updated);
  }, []);

  const setPresence = useCallback(async (state: 'available' | 'unavailable' | 'wrap_up') => {
    await api.post('/calls/presence', { state, sipRegistered: registration === 'registered' });
  }, [registration]);

  // ------------------------------------------------------------ recording

  const startRecording = useCallback(async () => {
    const call = activeCallRef.current;
    if (!call) return;
    const { recording: created } = await api.post<{ recording: { id: string } }>(
      `/calls/${call.id}/recording/start`,
    );
    recordingIdRef.current = created.id;
    setRecording(true);

    const streams = driverRef.current?.streams(call.sip_call_id) ?? [];
    if (streams.length === 0) {
      // The simulator has no audio. The recording *record* is still created, so
      // the incident file and the retention policy behave identically; there is
      // simply no audio blob to upload.
      return;
    }

    // Both directions are mixed into one track, so a reviewer hears the
    // conversation rather than two files they have to line up.
    const context = new AudioContext();
    const destination = context.createMediaStreamDestination();
    for (const stream of streams) {
      if (stream.getAudioTracks().length === 0) continue;
      context.createMediaStreamSource(stream).connect(destination);
    }

    const recorder = new MediaRecorder(destination.stream, { mimeType: 'audio/webm' });
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      void context.close().catch(() => undefined);
      const id = recordingIdRef.current;
      if (!id || chunksRef.current.length === 0) return;
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      chunksRef.current = [];
      void api.upload(`/calls/recordings/${id}/audio`, blob).catch(() => undefined);
    };
    recorder.start(1000);
    recorderRef.current = recorder;
  }, []);

  const stopRecording = useCallback(async () => {
    const call = activeCallRef.current;
    setRecording(false);
    recorderRef.current?.stop();
    recorderRef.current = null;
    if (call) await api.post(`/calls/${call.id}/recording/stop`).catch(() => undefined);
  }, []);

  const value = useMemo<SoftphoneState>(
    () => ({
      available: canHandleCalls,
      driver: driver?.name ?? null,
      registration,
      registrationDetail,
      queue,
      activeCall,
      muted,
      recording,
      error,
      refreshQueue,
      answer,
      hangup,
      setHold,
      toggleMute,
      dial,
      sendDtmf,
      transfer,
      startRecording,
      stopRecording,
      attachToIncident,
      setPresence,
    }),
    [
      canHandleCalls, driver, registration, registrationDetail, queue, activeCall, muted,
      recording, error, refreshQueue, answer, hangup, setHold, toggleMute, dial, sendDtmf,
      transfer, startRecording, stopRecording, attachToIncident, setPresence,
    ],
  );

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
      {/* The only audio sink in the app. Hidden, but present whenever a call can arrive. */}
      <audio ref={audioRef} autoPlay hidden />
    </SoftphoneContext.Provider>
  );
}

export function useSoftphone(): SoftphoneState {
  const context = useContext(SoftphoneContext);
  if (!context) throw new Error('useSoftphone must be used inside a SoftphoneProvider');
  return context;
}
